/**
 * `W5-T02` §6 — what the checkout shows, as i18n keys.
 *
 * A failed confirm comes back from Stripe.js as an error with a `type`, a `code` and sometimes a
 * `decline_code`. Its `message` is Stripe's text in Stripe's language and is **never** shown: the key
 * is chosen here and the copy is ours, in ES and EN. The server stores its own mapped code from the
 * webhook (`paymentErrorCode`, API side); the two agree on the cases that matter.
 *
 * The return page lands on whatever `GET /api/bookings/{id}` says — never on the browser's
 * "success", because only the webhook makes a booking `PAID` (AC 22).
 */
import { type BookingStatus } from '@marketplace/contracts';
import { type ApiError } from '../../shared/api-error.js';

export interface StripeJsError {
  readonly type: string;
  readonly code?: string;
  readonly decline_code?: string;
}

export type PaymentErrorKey =
  | 'booking.pay.insufficientFunds'
  | 'booking.pay.authFailed'
  | 'booking.pay.declined'
  | 'booking.pay.invalid'
  | 'booking.pay.failed';

export function paymentErrorKey(error: StripeJsError): PaymentErrorKey {
  if (error.decline_code === 'insufficient_funds') return 'booking.pay.insufficientFunds';
  if (error.code === 'payment_intent_authentication_failure') return 'booking.pay.authFailed';
  if (error.type === 'card_error') return 'booking.pay.declined';
  // The Payment Element's own field validation: the card number, the expiry, the CVC.
  if (error.type === 'validation_error') return 'booking.pay.invalid';
  return 'booking.pay.failed';
}

export type BookingStateKey =
  'booking.pay.pending' | 'booking.pay.expired' | 'booking.pay.refunded' | 'booking.pay.succeeded';

export function bookingStateKey(status: BookingStatus): BookingStateKey {
  switch (status) {
    case 'PENDING_PAYMENT':
      return 'booking.pay.pending';
    case 'EXPIRED':
      return 'booking.pay.expired';
    case 'REFUNDING':
    case 'REFUNDED':
      return 'booking.pay.refunded';
    default:
      // `PAID` and everything after it: the fee was paid.
      return 'booking.pay.succeeded';
  }
}

export type BookingErrorKey =
  | 'booking.error.notFound'
  | 'booking.error.forbidden'
  | 'booking.error.quoteNotPayable'
  | 'booking.error.jobNotAwardable'
  | 'booking.error.alreadyAwarded'
  | 'booking.error.unavailable';

/**
 * `W5-T15` §6 — an API refusal of `booking.create` or `booking.get`, as a key. The award button
 * (`W4-T17`) uses it too. A `401` never reaches here: the caller sends the visitor to log in.
 *
 * Anything this does not know — a `503`, a `429`, a request that never got an answer — is "payments
 * are unavailable": the one sentence that is true whichever it was.
 */
export function bookingErrorKey(error: ApiError): BookingErrorKey {
  switch (error.message) {
    case 'QUOTE_NOT_PAYABLE':
      return 'booking.error.quoteNotPayable';
    case 'JOB_NOT_AWARDABLE':
      return 'booking.error.jobNotAwardable';
    case 'JOB_ALREADY_AWARDED':
      return 'booking.error.alreadyAwarded';
  }
  if (error.status === 404) return 'booking.error.notFound';
  if (error.status === 403) return 'booking.error.forbidden';
  return 'booking.error.unavailable';
}
